import { useJobillaOrgId } from '@/hooks'
import { SubtleLoader } from '@/ui'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { subscriptionAccessDataReason, useEntitlementIndex } from '@jobilla/nexus-sdk'
import { Link } from '@tanstack/react-router'
import confetti from 'canvas-confetti'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const POLL_INTERVAL_MS = 3_000
const SLOW_AFTER_MS = 60_000
const CONFETTI_MS = 2_500
const CONFETTI_COLORS = ['#b365f6', '#9a4bf5', '#872cf0', '#710ce2', '#cb87fb', '#f0cefd']

/**
 * After 3DS the subscription only turns active once Stripe's webhook lands, so
 * access is awaited from entitlements rather than assumed from the checkout.
 */
export function ActivationStep({ companyId, planName }: { companyId: string; planName: string }) {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const [isSlow, setIsSlow] = useState(false)

	const { data } = useEntitlementIndex(organisationId, {
		enabled: !!organisationId,
		refetchInterval: (query) =>
			query.state.data?.subscriptionAccess.reason === subscriptionAccessDataReason.subscribed
				? false
				: POLL_INTERVAL_MS,
	})

	const isActive = data?.subscriptionAccess.reason === subscriptionAccessDataReason.subscribed

	useEffect(() => {
		const timeout = setTimeout(() => setIsSlow(true), SLOW_AFTER_MS)
		return () => clearTimeout(timeout)
	}, [])

	useEffect(() => {
		if (!isActive) {
			return
		}

		const end = Date.now() + CONFETTI_MS
		let animationFrame = 0

		const frame = () => {
			const cannon = { particleCount: 4, colors: CONFETTI_COLORS, spread: 55, disableForReducedMotion: true }
			void confetti({ ...cannon, angle: 60, origin: { x: 0, y: 0.6 } })
			void confetti({ ...cannon, angle: 120, origin: { x: 1, y: 0.6 } })

			if (Date.now() < end) {
				animationFrame = requestAnimationFrame(frame)
			}
		}

		frame()

		return () => cancelAnimationFrame(animationFrame)
	}, [isActive])

	if (isActive) {
		return (
			<CenteredCard>
				<CheckCircleIcon className="h-12 w-12 text-green-500" />
				<h1 className="text-xl font-bold text-grey-900">{t('subscribe.active.title', { plan: planName })}</h1>
				<p className="text-sm text-grey-600">{t('subscribe.active.description')}</p>
				<div className="flex items-center gap-3 pt-2">
					<Link
						to="/company/$companyId/settings/billing"
						params={{ companyId }}
						className="text-sm font-medium text-brand-700 hover:underline"
					>
						{t('subscribe.active.viewBilling')}
					</Link>
					<Link
						to="/company/$companyId"
						params={{ companyId }}
						className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
					>
						{t('subscribe.active.continue')}
					</Link>
				</div>
			</CenteredCard>
		)
	}

	return (
		<CenteredCard>
			<SubtleLoader delay={0} />
			<h1 className="text-lg font-semibold text-grey-900">{t('subscribe.processing.title')}</h1>
			<p className="text-sm text-grey-600">
				{isSlow ? t('subscribe.processing.slow') : t('subscribe.processing.description')}
			</p>
			{isSlow
				? (
					<Link
						to="/company/$companyId/settings/billing"
						params={{ companyId }}
						className="text-sm font-medium text-brand-700 hover:underline"
					>
						{t('subscribe.active.viewBilling')}
					</Link>
				)
				: null}
		</CenteredCard>
	)
}

function CenteredCard({ children }: { children: ReactNode }) {
	return (
		<div className="mx-auto flex max-w-lg flex-col items-center gap-4 rounded-xl border border-grey-200 bg-white p-10 text-center">
			{children}
		</div>
	)
}
